import { QuoteDrawer } from '@/components/Quotes/QuoteDrawer';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetDrawerMarginStyles } from '@/hooks/useGetDrawerMarginStyles';
import { useParentDivLayoutStyles } from '@/hooks/useParentDivLayoutStyles';
import usePermissions from '@/hooks/usePermissions';
import { useGetAccountQuery } from '@/services/accounts';
import { useGetContractsQuery, useSendContractV2Mutation, useVoidContractMutation } from '@/services/contract';
import { useFetchGeothermalAccountPricingMutation } from '@/services/pricing';
import { useCreateQuoteMutation, useGetQuotesQuery, useVoidQuoteMutation } from '@/services/quotes';
import {
  useCreateSystemDesignMutation,
  useGetCurrentDesignQuery,
  useLazyGetCurrentDesignQuery,
} from '@/services/systemDesign';
import { Alert, Box, Button, Modal, Spinner, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Formik, FormikErrors } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContractStatus, EquipmentPricingDetails, ProgramType, QuoteStatus, UserPermissions } from 'types';

import { useIsOrgReadOnly } from '../../auth/claimsUtils';
import RequirePermissions from '../../auth/requirePermissions';
import { EnhancedFormikForm } from '../../EnhancedFormikForm';
import { MainContainer } from '../../MainContainer';

import { SystemSection } from './SystemSection';

interface GeothermalFormValues {
  ecEligible?: boolean;
  systems: any[];
}

const extractCapacityTons = (capacityTons: any): number | undefined => {
  if (!capacityTons) return undefined;
  const raw =
    capacityTons && typeof capacityTons === 'object' && 'value' in capacityTons
      ? capacityTons.value
      : String(capacityTons);
  const parsed = parseFloat(raw);
  return isNaN(parsed) ? undefined : parsed;
};

const formatSystemData = (system: any) => {
  return {
    name: system.name,
    loopType: system.loopType,
    indoorUnitType: system.indoorUnitType,
    capacityTons: extractCapacityTons(system.capacityTons),
    dhwAssist: Boolean(system.dhwAssist),
    auxiliaryHeat: Boolean(system.auxiliaryHeat),
    auxiliaryHeatType: system.auxiliaryHeat ? system.auxiliaryHeatType : undefined,
    leaseAmount: parseFloat(String(system.leaseAmount)) || 0,
    manufacturer:
      system.manufacturer && typeof system.manufacturer === 'object' && 'value' in system.manufacturer
        ? system.manufacturer.value || undefined
        : system.manufacturer || undefined,
    equipment: system.equipment || [],
  };
};

const formatGeothermalData = (values: GeothermalFormValues, isPreliminary: boolean) => {
  const systems = values.systems.map(formatSystemData);
  return {
    isPreliminary,
    totalLeaseAmount: systems.reduce((sum: number, sys: any) => sum + (sys.leaseAmount || 0), 0),
    systems,
  };
};

const validateGeothermalForm = (values: GeothermalFormValues): FormikErrors<GeothermalFormValues> => {
  const errors: Record<string, any> = {};

  if (!values.systems?.length) {
    errors.systems = 'At least one system is required';
    return errors;
  }

  const systemErrors: any[] = [];
  values.systems.forEach((system: any, index: number) => {
    const sysErr: Record<string, string> = {};
    if (!system.loopType) sysErr.loopType = 'Loop type is required';
    if (!system.indoorUnitType) sysErr.indoorUnitType = 'Indoor unit type is required';
    if (!system.capacityTons) sysErr.capacityTons = 'Capacity is required';
    if (system.leaseAmount == null || system.leaseAmount === '' || system.leaseAmount === 0) {
      sysErr.leaseAmount = 'Lease amount is required';
    }
    const manufacturerValue =
      system.manufacturer && typeof system.manufacturer === 'object'
        ? system.manufacturer.value
        : system.manufacturer;
    if (!manufacturerValue) sysErr.manufacturer = 'Manufacturer is required';

    if (Object.keys(sysErr).length > 0) {
      systemErrors[index] = sysErr;
    }
  });

  if (systemErrors.length > 0) {
    errors.systems = systemErrors;
  }

  return errors;
};

const flattenErrors = (errors: FormikErrors<GeothermalFormValues>, values: GeothermalFormValues): string[] => {
  const messages: string[] = [];
  if (typeof errors.systems === 'string') {
    messages.push(errors.systems);
  } else if (Array.isArray(errors.systems)) {
    (errors.systems as any[]).forEach((sysErr: any, index: number) => {
      if (!sysErr) return;
      const name = values.systems[index]?.name || `System ${index + 1}`;
      Object.values(sysErr).forEach((msg) => {
        if (typeof msg === 'string') messages.push(`${name}: ${msg}`);
      });
    });
  }
  return messages;
};

export const GeothermalSystemDesign = () => {
  const targetRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { isOpen: isDrawerOpen, handleOpen: openDrawer, handleClose: closeDrawer } = useOpenClose();
  const parentStyles = useParentDivLayoutStyles(targetRef);
  const { id } = useParams<{ id: any }>();
  const [createQuote] = useCreateQuoteMutation();
  const drawerMarginStyles = useGetDrawerMarginStyles(parentStyles, isDrawerOpen);

  const { data: systemDesign } = useGetCurrentDesignQuery(
    { id, programType: ProgramType.geothermal },
    { skip: !id },
  );
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id, { skip: !id });
  const programType = account?.programType;
  const { data: quotes, isLoading: isQuotesLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType || !id },
  );
  const [voidQuote, { isLoading: isVoidingQuote }] = useVoidQuoteMutation({
    fixedCacheKey: 'useVoidQuoteMutationKey',
  });
  const { data: contracts, isLoading: areContractsLoading } = useGetContractsQuery(id, { skip: !id });
  const [fetchPricing, { data: pricingData = [], isLoading: isPricingLoading, status: pricingStatus }] =
    useFetchGeothermalAccountPricingMutation();

  const isLoading = isAccountLoading || isQuotesLoading || areContractsLoading;
  const [createSystemDesign, { isLoading: isCreatingSystemDesign }] = useCreateSystemDesignMutation();
  const [getCurrentSystemDesign, { data: createdSystemDesign }] = useLazyGetCurrentDesignQuery();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const {
    isOpen: isQuoteCreationModalOpen,
    handleOpen: openQuoteCreationModal,
    handleClose: closeQuoteCreationModal,
  } = useOpenClose();
  const { comfortPlanDealerConditions } = useFlags();
  const userPermissions = usePermissions();
  const [sendContract, { isLoading: isSendingContract }] = useSendContractV2Mutation({
    fixedCacheKey: 'useSendContractV2MutationKey',
  });
  const [formValues, setFormValues] = useState<any>({});
  const [createdQuote, setCreatedQuote] = useState<any>(null);
  const allowsAction = useIsOrgReadOnly();
  const [isPreliminary, setIsPreliminary] = useState(true);
  const [voidContract, { isLoading: isVoidingActiveContract }] = useVoidContractMutation({
    fixedCacheKey: 'useVoidContractMutationKey',
  });

  useEffect(() => {
    if (systemDesign) {
      setIsPreliminary(systemDesign.isPreliminary);
    }
  }, [systemDesign]);

  const activeContract = useMemo(() => {
    return contracts?.find(
      (contract: any) => ![ContractStatus.voided, ContractStatus.rejected].includes(contract.status),
    );
  }, [contracts]);

  const systemDesignCost = useMemo(() => {
    const design = createdSystemDesign || systemDesign;
    if (!design) return 0;
    return design.totalLeaseAmount || 0;
  }, [createdSystemDesign, systemDesign]);

  const activeQuote = quotes?.find((quote: any) => quote.status === QuoteStatus.active);

  const toggleDrawer = useCallback(() => {
    if (isDrawerOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }, [isDrawerOpen, closeDrawer, openDrawer]);

  const handleSubmit = async (data: GeothermalFormValues, { setSubmitting }: any) => {
    try {
      setFormValues(data);
      const formatted = formatGeothermalData(data, isPreliminary);
      if (activeQuote && formatted.totalLeaseAmount !== activeQuote.totalSystemCost) {
        openModal();
        return;
      }
      await createSystemDesign({
        systemDesign: formatGeothermalData(data, isPreliminary),
        accountId: id,
        programType: ProgramType.geothermal,
      }).unwrap();
      toast.success('System design saved successfully');
      getCurrentSystemDesign({ id, programType: ProgramType.geothermal });
      await fetchPricing({ accountId: id, useCurrentSystemDesign: true });
      openDrawer();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error saving system design');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmModalSubmit = async () => {
    closeModal();
    if (formValues) {
      try {
        if (activeContract) {
          await voidContract({
            accountId: String(id),
            contractId: String(activeContract.id),
          }).unwrap();
          toast.success('Active Contract voided successfully');
        }
        await voidQuote({ accountId: id.toString(), quoteId: activeQuote.id.toString() }).unwrap();
        toast.success('Quote voided successfully');
        await createSystemDesign({
          systemDesign: formatGeothermalData(formValues, isPreliminary),
          accountId: id,
          programType: ProgramType.geothermal,
        }).unwrap();
        toast.success('System design saved successfully');
        getCurrentSystemDesign({ id, programType: ProgramType.geothermal });
        await fetchPricing({ accountId: id, useCurrentSystemDesign: true });
        openDrawer();
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error saving system design');
      }
    }
  };

  const isVoiding = isVoidingQuote || isVoidingActiveContract;

  const ratesAndTerms = useMemo(() => {
    if (!pricingData || !pricingData.length) {
      return { rates: [], terms: [] };
    }
    const rates = Array.from(new Set(pricingData.map((item: any) => item.escalationRate)))
      .toSorted((a: any, b: any) => a - b)
      .map((rate: any) => ({
        label: `${(rate * 100).toFixed(2)}%`,
        value: rate.toString(),
        id: rate.toString(),
      }));
    const terms = Array.from(new Set(pricingData.map((item: any) => item.term)))
      .toSorted((a: any, b: any) => a - b)
      .map((term: any) => ({
        label: `${term} years`,
        value: term.toString(),
        id: term.toString(),
      }));
    return { rates, terms };
  }, [pricingData]);

  const handleCreateQuote = useCallback(
    async (values: any) => {
      const selectedProduct = pricingData.find(
        (product: any) =>
          product.term === parseInt(values.term) && product.escalationRate === parseFloat(values.rate),
      );

      if (!selectedProduct) {
        toast.error('No matching pricing found for selected term and rate');
        return;
      }

      const creationPayload = {
        quoteData: {
          productId: selectedProduct.productId,
          externalReference: account?.externalReference || undefined,
          totalFinancedAmount: systemDesignCost,
        },
        id: account.id,
        programType: ProgramType.geothermal,
      } as any;

      try {
        const quote = await createQuote(creationPayload).unwrap();
        const userIsAllowedToSendContract =
          comfortPlanDealerConditions && userPermissions.includes(UserPermissions.comfortPlanDealer);
        if (userIsAllowedToSendContract && quote?.status === QuoteStatus.active) {
          setCreatedQuote(quote);
          openQuoteCreationModal();
        } else {
          navigate(`/accounts/${id}`);
          toast.success('Quote created successfully');
        }
      } catch (e) {
        toast.error('Error creating quote');
        console.error(e);
      }
    },
    [
      pricingData,
      account,
      systemDesignCost,
      createQuote,
      comfortPlanDealerConditions,
      userPermissions,
      openQuoteCreationModal,
      navigate,
      id,
    ],
  );

  const handleSendContract = useCallback(async () => {
    const toastId = toast.loading('Sending contract');
    try {
      if (activeContract?.quoteId === activeQuote?.id) {
        await sendContract({
          accountId: String(activeContract?.accountId),
          contractId: String(activeContract?.id),
          forceSend: true,
        }).unwrap();
        navigate(`/accounts/${id}`);
        toast.success('Contract sent successfully');
      } else {
        toast.error('Unable to send contract: contract and active quote do not match');
      }
    } catch (e: any) {
      if (e?.data?.errors) {
        toast.error(e?.data?.errors.join(', '));
      } else {
        toast.error(e?.data?.message || 'Error sending contract');
      }
    } finally {
      toast.dismiss(toastId);
    }
  }, [activeContract, activeQuote, sendContract, navigate, id]);

  const handleCloseQuoteCreationModal = useCallback(() => {
    closeQuoteCreationModal();
    navigate(`/accounts/${id}`);
  }, [closeQuoteCreationModal, navigate, id]);

  const defaultSystem = {
    name: 'System 1',
    loopType: undefined,
    indoorUnitType: undefined,
    capacityTons: undefined,
    dhwAssist: false,
    auxiliaryHeat: false,
    auxiliaryHeatType: '',
    leaseAmount: 0,
    manufacturer: undefined,
    equipment: [],
  };

  const initialValues: GeothermalFormValues = useMemo(() => {
    if (!systemDesign) {
      return {
        ecEligible: undefined,
        systems: [defaultSystem],
      };
    }

    const systems = (systemDesign.systems || []).map((sys: any, i: number) => ({
      ...sys,
      name: sys.name || `System ${i + 1}`,
      capacityTons:
        sys.capacityTons != null
          ? { label: `${sys.capacityTons} tons`, value: sys.capacityTons.toString() }
          : undefined,
      leaseAmount: sys.leaseAmount || 0,
      manufacturer: (() => {
        const mfg = sys.manufacturer || sys.equipment?.[0]?.manufacturer;
        return mfg ? { label: mfg, value: mfg } : undefined;
      })(),
      equipment: sys.equipment || [],
    }));

    return {
      ecEligible: systemDesign.ecEligible,
      systems: systems.length ? systems : [defaultSystem],
    };
  }, [systemDesign]);

  return (
    <>
      <MainContainer>
        {isLoading ? (
          <Box display="block" textAlign="center" padding="lg" childGap="xl">
            <Spinner size="lg" />
          </Box>
        ) : (
          <Box
            overflow="hidden"
            childGap="lg"
            ref={targetRef}
            style={{
              marginRight: drawerMarginStyles,
              position: 'relative',
              transition: 'margin-right 0.3s ease',
            }}
          >
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              enableReinitialize={true}
              validateOnChange={true}
              validateOnMount={true}
              validate={validateGeothermalForm}
            >
              {({ values, isSubmitting, errors, submitCount }) => {
                const totalLeaseAmount = values.systems.reduce(
                  (sum: number, sys: any) => sum + (parseFloat(String(sys.leaseAmount)) || 0),
                  0,
                );
                return (
                  <EnhancedFormikForm noValidate>
                    {account?.propertyDetails && (
                      <Box gap="lg">
                        <Box
                          borderColor="separator"
                          borderWidth="0 0 xs 0"
                          padding={{ desktop: 'lg 0 xl 0', base: 'lg 3xl' }}
                          direction="row"
                          childGap="5xl"
                          fontSize="md"
                          fontWeight="medium"
                        >
                          <Box>Account Details</Box>
                          <Box childGap="sm" padding={{ base: '0 0 0 5xl' }}>
                            {account?.propertyDetails?.homeSize && (
                              <Box>Home Size (sq ft): {account?.propertyDetails?.homeSize}</Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}

                    <SystemSection isManufacturerDisabled={!!activeContract} />

                    <Box alignItems="flex-end" margin="md" padding={{ base: 'lg 0 0 0' }} gap="xs">
                      <Box direction="row" gap="xs" alignItems="center">
                        <Box fontWeight="medium" fontSize="md">
                          Total Lease Amount:
                        </Box>
                        <Box fontWeight="bold" fontSize="lg">
                          {currencyFormatter.format(totalLeaseAmount)}
                        </Box>
                      </Box>
                    </Box>

                    {submitCount > 0 && Object.keys(errors).length > 0 && (
                      <Box padding="sm 0">
                        <Alert
                          variant="danger"
                          hasIcon
                          title="Please fix the following errors:"
                          render={() => (
                            <Box as="ul" gap="xs" fontSize="sm">
                              {flattenErrors(errors, values).map((error, i) => (
                                <li key={i}>{error}</li>
                              ))}
                            </Box>
                          )}
                        />
                      </Box>
                    )}

                    <Box
                      direction={{ base: 'column', tablet: 'row' }}
                      childGap="sm"
                      gap="lg"
                      style={{ flexShrink: 0 }}
                      padding={{ base: 'lg' }}
                      background="primary"
                      wrap={true}
                    >
                      {activeQuote ? (
                        <Box alignSelf="flex-start" direction="row" gap="lg">
                          <Box gap="xs">
                            <Box fontSize="xl" fontWeight="bold">
                              {currencyFormatter.format(activeQuote.totalSystemCost)}
                            </Box>
                            <Box fontSize="md">Total Financed Amount</Box>
                          </Box>
                          <Box gap="xs">
                            <Box direction="row" alignItems="center" gap="2xs">
                              <Box fontSize="xl" fontWeight="bold">
                                {currencyFormatter.format(
                                  activeQuote.systemPricingDetails.find((d: EquipmentPricingDetails) => d.year === 1)
                                    ?.monthlyPayment || 0,
                                )}
                              </Box>
                              <Box fontSize="md">/mo</Box>
                            </Box>
                            <Box fontSize="md">Year 1 Monthly Payment</Box>
                          </Box>
                          <Box gap="xs">
                            <Box direction="row" alignItems="center" gap="2xs">
                              <Box fontSize="xl" fontWeight="bold">
                                {activeQuote.systemPricingDetails.length}
                              </Box>
                              <Box fontSize="md">
                                {activeQuote.systemPricingDetails.length > 1 ? 'years' : 'year'}
                              </Box>
                            </Box>
                            <Box fontSize="md">Customer Term Length</Box>
                          </Box>
                        </Box>
                      ) : null}
                      <RequirePermissions
                        permissions={[UserPermissions.admin, UserPermissions.editor]}
                        checkAllPermissions={false}
                      >
                        <Box style={{ marginLeft: 'auto' }} direction="row" gap="sm">
                          <Button
                            as="a"
                            href={`/accounts/${id ? id : ''}`}
                            variant="secondary"
                            tone="neutral"
                            size="md"
                            isDisabled={isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="md"
                            variant="primary"
                            type="submit"
                            isDisabled={isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding}
                            isLoading={isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding}
                          >
                            Save &amp; Get Pricing
                          </Button>
                        </Box>
                      </RequirePermissions>
                    </Box>
                  </EnhancedFormikForm>
                );
              }}
            </Formik>
            {!pricingData.length && pricingStatus !== 'uninitialized' && !isPricingLoading ? (
              <Alert variant="info" message="No pricing options available for this system design" />
            ) : null}
            <QuoteDrawer
              isOpen={isDrawerOpen}
              toggleDrawer={toggleDrawer}
              ratesAndTerms={ratesAndTerms}
              handleCreateQuote={handleCreateQuote}
              isLoading={isPricingLoading || isCreatingSystemDesign || isVoiding}
              systemDesignCost={systemDesignCost}
              isPricingLoading={isPricingLoading}
              pricingData={pricingData}
              programType={ProgramType.geothermal}
            />
          </Box>
        )}
      </MainContainer>

      <Modal ariaLabelledBy="costMismatch" isOpen={isModalOpen} onDismiss={closeModal}>
        <Modal.Header id="costMismatch" title="Cost Mismatch" onDismiss={closeModal} />
        <Modal.Body>
          The total lease amount does not match the active quote cost. Would you like to void the existing quote{' '}
          {activeContract ? 'and active contract' : ''} and create a new one?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmModalSubmit} disabled={!allowsAction}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        ariaLabelledBy="quoteCreationModal"
        isOpen={isQuoteCreationModalOpen}
        onDismiss={handleCloseQuoteCreationModal}
      >
        <Modal.Header
          id="quoteCreationModal"
          title="Quote Created Successfully"
          onDismiss={handleCloseQuoteCreationModal}
        />
        <Modal.Body>
          <Box gap="md" direction="column">
            <Box display="block">
              You can now send the contract to the customer for signature. To send them their contract for a{' '}
              {<strong>{createdQuote?.systemPricingDetails?.length}</strong>}{' '}
              {(createdQuote?.systemPricingDetails?.length ?? 0) > 1 ? 'years' : 'year'} Comfort Plan with an Initial
              Monthly Payment of{' '}
              {
                <strong>
                  {currencyFormatter.format(
                    createdQuote?.systemPricingDetails?.find((d: EquipmentPricingDetails) => d.year === 1)
                      ?.monthlyPayment ?? 0,
                  )}
                </strong>
              }
              , click the button below.
            </Box>
            <Box display="block">
              The customer will also be invited to create their Palmetto Account where they must complete all the
              Notice to Proceed steps. The customer must complete all steps in their account in order to achieve NTP.
              Our chat support team is standing by to assist you!
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSendContract} disabled={isSendingContract}>
            Send Contract
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
